import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

function ExportQuotes({ token }) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({
    vehicle_no: '',
    status: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchQuotes();
    fetchVehicles();
    fetchCompletedJobs();
    fetchVendors();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.vehicle_no) params.append('vehicle_no', filters.vehicle_no);
      if (filters.status) params.append('status', filters.status);
      
      const response = await fetch(`${BACKEND_URL}/api/quotes/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/completed-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedJobs(data);
      }
    } catch (error) {
      console.error('Error fetching completed jobs:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const exportToCSV = () => {
    if (!quotes.data || quotes.data.length === 0) {
      setMessage({ type: 'error', text: 'No data to export' });
      return;
    }

    const headers = quotes.columns.join(',');
    const rows = quotes.data.map(row => 
      quotes.columns.map(col => {
        const val = row[col];
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val ?? '';
      }).join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quotes_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    setMessage({ type: 'success', text: 'CSV exported successfully' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleVendorUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('vendor_file', file);

      const response = await fetch(`${BACKEND_URL}/api/vendors/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        fetchVendors();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Upload failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload vendors' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const toggleJobSelection = (repairId) => {
    setSelectedJobs(prev => 
      prev.includes(repairId) 
        ? prev.filter(id => id !== repairId)
        : [...prev, repairId]
    );
  };

  const selectAllJobs = () => {
    if (selectedJobs.length === completedJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(completedJobs.map(j => j.repair_id));
    }
  };

  const exportToNavision = async (format = 'excel') => {
    if (selectedJobs.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one job to export' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      selectedJobs.forEach(id => formData.append('repair_ids', id));

      const endpoint = format === 'csv' ? '/api/navision/export' : '/api/navision/export-editable';
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        const link = document.createElement('a');
        if (format === 'csv') {
          link.href = `data:text/csv;base64,${data.csv}`;
        } else {
          link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.excel}`;
        }
        link.download = data.filename;
        link.click();

        setMessage({ 
          type: 'success', 
          text: `Exported ${data.row_count} line items from ${data.repair_count} repairs` 
        });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Export failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to export' });
    } finally {
      setLoading(false);
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount);
  };

  return (
    <div data-testid="export-quotes-page">
      <div className="page-header">
        <h2 style={isMobile ? { fontSize: '1.25rem' } : {}}>Data Export</h2>
        <p style={isMobile ? { fontSize: '0.8rem' } : {}}>Export quotes to CSV and completed jobs to Navision format</p>
      </div>

      {/* Toast Message */}
      {message && (
        <div 
          className={`alert alert-${message.type}`}
          style={{ 
            position: 'fixed', 
            top: '20px', 
            right: isMobile ? '1rem' : '20px',
            left: isMobile ? '1rem' : 'auto',
            zIndex: 9999,
            minWidth: isMobile ? 'auto' : '300px',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          {message.text}
        </div>
      )}

      {/* Tabs - Scrollable on mobile */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        borderBottom: '2px solid var(--gray-200)',
        ...(isMobile ? {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          marginLeft: '-1rem',
          marginRight: '-1rem',
          paddingLeft: '1rem',
          paddingRight: '1rem',
        } : {})
      }}>
        <button 
          className={`btn ${activeTab === 'quotes' ? '' : 'btn-secondary'}`}
          style={{ 
            background: activeTab === 'quotes' ? 'var(--ptc-navy)' : undefined,
            color: activeTab === 'quotes' ? 'white' : undefined,
            borderRadius: '8px 8px 0 0',
            borderBottom: activeTab === 'quotes' ? '2px solid var(--ptc-red)' : 'none',
            marginBottom: '-2px',
            ...(isMobile ? { fontSize: '0.75rem', padding: '0.4rem 0.6rem', whiteSpace: 'nowrap', flexShrink: 0 } : {})
          }}
          onClick={() => setActiveTab('quotes')}
        >
          📊 {isMobile ? 'CSV' : 'Quote Export (CSV)'}
        </button>
        <button 
          className={`btn ${activeTab === 'navision' ? '' : 'btn-secondary'}`}
          style={{ 
            background: activeTab === 'navision' ? 'var(--ptc-navy)' : undefined,
            color: activeTab === 'navision' ? 'white' : undefined,
            borderRadius: '8px 8px 0 0',
            borderBottom: activeTab === 'navision' ? '2px solid var(--ptc-red)' : 'none',
            marginBottom: '-2px',
            ...(isMobile ? { fontSize: '0.75rem', padding: '0.4rem 0.6rem', whiteSpace: 'nowrap', flexShrink: 0 } : {})
          }}
          onClick={() => setActiveTab('navision')}
        >
          📤 Navision
        </button>
        <button 
          className={`btn ${activeTab === 'vendors' ? '' : 'btn-secondary'}`}
          style={{ 
            background: activeTab === 'vendors' ? 'var(--ptc-navy)' : undefined,
            color: activeTab === 'vendors' ? 'white' : undefined,
            borderRadius: '8px 8px 0 0',
            borderBottom: activeTab === 'vendors' ? '2px solid var(--ptc-red)' : 'none',
            marginBottom: '-2px',
            ...(isMobile ? { fontSize: '0.75rem', padding: '0.4rem 0.6rem', whiteSpace: 'nowrap', flexShrink: 0 } : {})
          }}
          onClick={() => setActiveTab('vendors')}
        >
          🏪 Vendors ({vendors.length})
        </button>
      </div>

      {/* Quote Export Tab */}
      {activeTab === 'quotes' && (
        <>
          <div className="card" style={{ marginBottom: '1.5rem', padding: isMobile ? '1rem' : undefined }}>
            <h3 className="card-title" style={isMobile ? { fontSize: '1rem' } : {}}>Filter Quotes</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
              <div className="form-group" style={{ flex: '1', minWidth: isMobile ? 'auto' : '200px', marginBottom: 0 }}>
                <label className="form-label">Vehicle</label>
                <select 
                  className="form-select"
                  value={filters.vehicle_no}
                  onChange={(e) => setFilters({...filters, vehicle_no: e.target.value})}
                >
                  <option value="">All Vehicles</option>
                  {vehicles.map(v => (
                    <option key={v.vehicle_no} value={v.vehicle_no}>{v.vehicle_no}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: '1', minWidth: '200px', marginBottom: 0 }}>
                <label className="form-label">Status</label>
                <select 
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="">All Statuses</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                <button className="btn btn-primary" onClick={fetchQuotes} disabled={loading}>
                  {loading ? 'Loading...' : '🔍 Apply Filters'}
                </button>
                <button className="btn btn-success" onClick={exportToCSV} disabled={!quotes.data?.length}>
                  📥 Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Quote Data Preview</h3>
            {loading ? (
              <div className="spinner"></div>
            ) : quotes.data?.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      {quotes.columns?.map(col => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.data.slice(0, 20).map((row, idx) => (
                      <tr key={idx}>
                        {quotes.columns?.map(col => (
                          <td key={col}>
                            {typeof row[col] === 'number' && col.toLowerCase().includes('cost') 
                              ? formatCurrency(row[col]) 
                              : String(row[col] ?? '-').substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {quotes.data.length > 20 && (
                  <p style={{ textAlign: 'center', color: 'var(--gray-500)', marginTop: '1rem' }}>
                    Showing 20 of {quotes.data.length} rows. Export to see all data.
                  </p>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📊</div>
                <p className="empty-state-title">No quotes found</p>
                <p>Adjust filters or create some repair requests first</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Navision Export Tab */}
      {activeTab === 'navision' && (
        <>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 className="card-title" style={{ marginBottom: 0 }}>Select Completed Jobs for Navision Export</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={selectAllJobs}
                >
                  {selectedJobs.length === completedJobs.length ? '☐ Deselect All' : '☑ Select All'}
                </button>
                <button 
                  className="btn"
                  style={{ background: 'var(--ptc-navy)', color: 'white' }}
                  onClick={() => exportToNavision('excel')}
                  disabled={selectedJobs.length === 0 || loading}
                >
                  📊 Export Excel (Editable)
                </button>
                <button 
                  className="btn btn-success"
                  onClick={() => exportToNavision('csv')}
                  disabled={selectedJobs.length === 0 || loading}
                >
                  📥 Export CSV (Direct Import)
                </button>
              </div>
            </div>
            
            {selectedJobs.length > 0 && (
              <div style={{ 
                background: 'var(--success-light)', 
                padding: '0.75rem', 
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <strong>{selectedJobs.length}</strong> job(s) selected for export
              </div>
            )}

            {completedJobs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📁</div>
                <p className="empty-state-title">No completed jobs</p>
                <p>Complete jobs through the approval workflow to export them</p>
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table">
                  <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input 
                          type="checkbox"
                          checked={selectedJobs.length === completedJobs.length}
                          onChange={selectAllJobs}
                        />
                      </th>
                      <th>Vehicle</th>
                      <th>Description</th>
                      <th>Invoice #</th>
                      <th style={{ textAlign: 'right' }}>Approved</th>
                      <th style={{ textAlign: 'right' }}>Invoice</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedJobs.map(job => {
                      const invoice = job.repair?.invoice || {};
                      return (
                        <tr 
                          key={job.repair_id}
                          style={{ 
                            background: selectedJobs.includes(job.repair_id) ? 'var(--success-light)' : undefined,
                            cursor: 'pointer'
                          }}
                          onClick={() => toggleJobSelection(job.repair_id)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input 
                              type="checkbox"
                              checked={selectedJobs.includes(job.repair_id)}
                              onChange={() => toggleJobSelection(job.repair_id)}
                            />
                          </td>
                          <td style={{ fontFamily: 'JetBrains Mono', fontWeight: '600', color: 'var(--ptc-navy)' }}>
                            {job.vehicle_no}
                          </td>
                          <td>{job.repair?.description?.substring(0, 40) || '-'}...</td>
                          <td style={{ fontFamily: 'JetBrains Mono' }}>{invoice.invoice_number || '-'}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono' }}>
                            {formatCurrency(job.estimated_cost)}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono' }}>
                            {formatCurrency(invoice.final_amount)}
                          </td>
                          <td style={{ color: 'var(--success)' }}>
                            {job.repair?.completed_at 
                              ? new Date(job.repair.completed_at).toLocaleDateString() 
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <h3 className="card-title">Navision Export Format</h3>
            <div style={{ 
              background: 'var(--gray-50)', 
              padding: '1rem', 
              borderRadius: '8px',
              fontSize: '0.85rem'
            }}>
              <p style={{ marginBottom: '0.5rem' }}><strong>Export Options:</strong></p>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--gray-600)' }}>
                <li><strong>Excel (Editable)</strong> - Includes vendor dropdown, highlighted fields for manual input (PTC Charge Code, Workshop Item Code), and vendor reference sheet</li>
                <li><strong>CSV (Direct Import)</strong> - Ready for Navision import, but may need vendor codes added</li>
              </ul>
              <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                <strong>Columns:</strong> Transaction Type, Vendor No, Vendor Ref No, Document Date, PTC Charge Code, Description, Quantity, Unit Price, GST, GST Amount, Amount Before GST, Amount After GST, Delivery No., Delivery Date, PTC Ref No., PTC Order Remark, Workshop Item Code, Customer, Dept, Veh Code
              </p>
            </div>
          </div>
        </>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h3 className="card-title">Upload Vendor List</h3>
            <p style={{ color: 'var(--gray-500)', marginBottom: '1rem' }}>
              Upload your vendor list Excel file (.xls or .xlsx) with columns: No., Name, County
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={handleVendorUpload}
                disabled={uploading}
                style={{ flex: 1 }}
              />
              {uploading && <span style={{ color: 'var(--gray-500)' }}>Uploading...</span>}
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 className="card-title" style={{ marginBottom: 0 }}>Vendor List ({vendors.length})</h3>
              <input
                type="text"
                className="form-input"
                placeholder="Search vendors..."
                style={{ maxWidth: '300px' }}
                onChange={(e) => {
                  const q = e.target.value.toLowerCase();
                  if (!q) {
                    fetchVendors();
                  } else {
                    setVendors(prev => prev.filter(v => 
                      v.vendor_no?.toLowerCase().includes(q) || 
                      v.name?.toLowerCase().includes(q)
                    ));
                  }
                }}
              />
            </div>

            {vendors.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🏪</div>
                <p className="empty-state-title">No vendors uploaded</p>
                <p>Upload your vendor list Excel file to get started</p>
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table">
                  <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                    <tr>
                      <th style={{ width: '120px' }}>Vendor No</th>
                      <th>Name</th>
                      <th style={{ width: '100px' }}>County</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.slice(0, 100).map((vendor, idx) => (
                      <tr key={idx}>
                        <td style={{ fontFamily: 'JetBrains Mono', fontWeight: '600', color: 'var(--ptc-navy)' }}>
                          {vendor.vendor_no}
                        </td>
                        <td>{vendor.name}</td>
                        <td style={{ color: 'var(--gray-500)' }}>{vendor.county || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {vendors.length > 100 && (
                  <p style={{ textAlign: 'center', color: 'var(--gray-500)', marginTop: '1rem' }}>
                    Showing 100 of {vendors.length} vendors. Use search to find specific vendors.
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ExportQuotes;
